import jwt from 'jsonwebtoken';
import axios from 'axios';

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import Configurations from 'src/settings';

import type { AuthContextType, AuthProviderProps } from '.';
import { Authorization, GetAuthTokenRequest, LegacyPayload } from 'src/types';
import { AuthToken, getTokenData } from 'src/utils/token';
import {
  BROADCAST_CHANNEL_AUTH_ID,
  BROADCAST_CHANNEL_CODE,
  broadcastChannelLogout,
  broadcastChannelReload,
} from 'src/utils/helpers/broadcast-channel';
import { deleteCookie, setCookie } from 'src/utils/cookie';
import {
  appstatetokenName,
  isLoggingOut,
  limitedloginPreviousUrlName,
  loginPreviousUrlName,
  memberCookieName,
  MemberType,
  Paths,
  relogintokenName,
  storedCommoditiestokenName,
  themetokenName,
  tokenCookieName,
} from 'src/utils/constants';

import Api from 'src/store/services/api';
import { clearTokenCookie, deleteAspCookies, isExpiredToken } from 'src/utils/helpers';
import { constants } from 'src/utils/settings';
import { getAuthToken } from 'src/service/auth';
import encryptPassword from './providers/encryptionHelper';
import { getAuthData } from 'src/store/services/auth';
import { AUTH_UNAUTHORIZED_EVENT } from 'src/utils/message';
import { history } from 'src/utils/history';

/* istanbul ignore next */
export enum AuthUserType {
  Anonymous = 'A',
  DSUser = 'DS',
  Initial = '',
  Ops = 'O',
}

/* istanbul ignore next */
export enum AuthKey {
  AccountCreatedDate = 'acd',
  AccountId = 'us',
  AccountLocked = 'lkd',
  AccountLockedMinutes = 'lm',
  AccountPermissions = 'prms',
  DisabledNotifications = 'dn',
  Email = 'eml',
  FirstName = 'fn',
  FreeMemberId = 'fmi',
  FullName = 'fln',
  HasError = 'her',
  LastLogin = 'll',
  LastName = 'ln',
  MainContact = 'mc',
  MemberBlackListed = 'mbl',
  MemberCreatedDate = 'mcd',
  MemberId = 'mi',
  MembershipLevels = 'ml',
  MemberStatus = 'ms',
  MemberType = 'mt',
  Migrated = 'mgrtd',
  OpsId = 'opi',
  PrimaryMemberId = 'pmid',
  ProductType = 'pt',
  Theme = 'tm',
  Token = 'tk',
  Username = 'un',
  /** @warning this is different from our standard use of userType. Should be typed as AuthUserType */
  AuthUserType = 'ut',
}

export const AuthContext = createContext<AuthContextType>({
  authIsLoading: true,
  token: '',
  handleLogin: async () => {},
  handleLimitedPageLogin: async () => {},
  handleLogout: () => {},
  handleStoredLogin: async () => false,
  handleESNLogin: () => {},
  isTokenExpired: () => false,
  resetAuth: () => {},
  refreshAuth: () => {},
  loginError: '',
});

export function useAuthContext() {
  return useContext(AuthContext);
}

const AuthProvider = ({ children }: AuthProviderProps) => {
  const [auth, setAuth] = useState<Authorization>();
  const [authIsLoading, setAuthIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState(false);
  const [loginError, setloginError] = useState('');
  const [token, setToken] = useState<string>('');
  const [memberId, setMemberId] = useState<number>();
  const [isAuthenticated, setAuthenticated] = useState<boolean>(false);
  const authRef = useRef<Authorization>();
  const authRequestId = useRef(0);
  const unauthorizedRedirect = useRef<ReturnType<typeof setTimeout>>();
  const storedLoginRequested = useRef(false);
  const authChannel = useRef<BroadcastChannel>();
  const getAuthChannel = useCallback(() => {
    if (!authChannel.current) {
      authChannel.current = new BroadcastChannel(BROADCAST_CHANNEL_AUTH_ID);
    }
    return authChannel.current;
  }, []);

  const clearAuthState = useCallback(() => {
    authRef.current = undefined;
    setAuth(undefined);
    setToken('');
    setMemberId(undefined);
    setAuthenticated(false);
  }, []);

  const beginAuthRequest = () => {
    setAuthIsLoading(true);
    return ++authRequestId.current;
  };

  /**
   * @returns: {TokenExpiration} boolean
   * @description: Checks if auth context token is expired and returns a boolean
   */
  const isTokenExpired = useCallback(() => {
    if (token) {
      const decodedToken = jwt.decode(token) as AuthToken;
      // Assert(tokenData, 'We should not get a null value when decoding tokenData', 'helpers/auth.ts');
      if (decodedToken && decodedToken.exp && Date.now() > decodedToken.exp * 1000) {
        return true;
      }
      return false;
    } else {
      return false;
    }
  }, [token]);

  /**
   * @returns: {Token} string
   * @description: Fetches newest token from API for current auth context
   */
  const getRefreshToken = useCallback(async () => {
    const data = {};
    const response: any = await Api.postRequestWithAuthentication(Paths.auth.refreshToken, data, {
      baseURL: constants.api.authUrl,
    });
    console.log('refreshToken',response);

    return response;
  }, []);

  const resetAuth = () => {
    handleLogout();
  };
  const processAuthData = useCallback(
    async (authData: { data: Authorization }, requestId = authRequestId.current): Promise<boolean> => {
      if (requestId !== authRequestId.current) {
        return false;
      }
      if (!authData?.data?.token || authData.data.validatedResult?.status !== true) {
        throw new Error('The authentication session is invalid.');
      }
      const decodedToken = jwt.decode(authData.data.token) as AuthToken;
      if (decodedToken && decodedToken.exp && Date.now() > decodedToken.exp * 1000) {
        const res = await getRefreshToken();
        if (requestId !== authRequestId.current) {
          return false;
        }
        if (res.status !== 200) {
          throw new Error('Unable to refresh the authentication session.');
        }
        const refreshedAuth = await getAuthData();
        if (requestId !== authRequestId.current) return false;
        if (!refreshedAuth?.data?.token ||
            refreshedAuth.data.validatedResult?.status !== true ||
            isExpiredToken(refreshedAuth.data.token)) {
          throw new Error('The refreshed authentication session is invalid.');
        }
        return processAuthData(refreshedAuth, requestId);
      }
      authRef.current = authData.data;
      if (unauthorizedRedirect.current !== undefined) {
        clearTimeout(unauthorizedRedirect.current);
        unauthorizedRedirect.current = undefined;
      }
      setMemberId(authData?.data?.memberId);
      setToken(authData?.data?.token);
      setAuth(authData?.data);
      setCookie(memberCookieName, authData?.data?.memberId, 1);
      if (authData?.data?.token) {
        setAuthenticated(true);
      }
      setAuthIsLoading(false);
      sessionStorage.removeItem(isLoggingOut);
      setHasError(false);
      setloginError('');
      return true;
    },
    [getRefreshToken],
  );

  /**
   * @param: {GetAuthTokenRequest} payload
   * @description: Fetches token from API and sets auth context for current user
   */
  const handleLogin = async ({ userName, password }: GetAuthTokenRequest) => {
    const requestId = beginAuthRequest();
    try {
      password = encryptPassword(userName, password);
      const hashed = true;

      const response: any = await getAuthToken({ userName, password, hashed });
      if (requestId !== authRequestId.current) return;
      const isloginFailed = response?.errorMessage !== undefined;
      setHasError(isloginFailed);
      setloginError(response?.errorMessage || '');

      if (isloginFailed) {
        setAuthIsLoading(false);
        return;
      }

      const authData = await getAuthData();
      if (await processAuthData(authData, requestId) && requestId === authRequestId.current) {
        broadcastChannelReload(getAuthChannel());
      }
    } catch (error) {
      console.warn(error);
      if (requestId === authRequestId.current) {
        setHasError(true);
        setAuthIsLoading(false);
      }
    }
  };

  const handleLimitedPageLogin = async ({
    userName,
    password,
    pagefor,
    orderBidPackage,
    pageBid,
  }: LegacyPayload) => {
    const requestId = beginAuthRequest();
    try {
      password = encryptPassword(userName, password);
      const hashed = true;
      const response: any = await getAuthToken({ userName, password, hashed });
      if (requestId !== authRequestId.current) return;
      const isloginFailed = response?.errorMessage !== undefined;
      setHasError(isloginFailed);
      setloginError(response?.errorMessage || '');

      if (isloginFailed) {
        setAuthIsLoading(false);
        return;
      }

      const authData = await getAuthData();
      if (!await processAuthData(authData, requestId) || requestId !== authRequestId.current) return;
      broadcastChannelReload(getAuthChannel());

      if (!authData?.data?.hasError && pagefor === 'popup' && orderBidPackage === true) {
        sessionStorage.setItem('orderBidPackage', 'checkDownload');
      } else if (pagefor === 'popup' && pageBid) {
        sessionStorage.setItem(limitedloginPreviousUrlName, `/bids/${pageBid}/details`);
      }
    } catch (error) {
      console.warn(error);
      if (requestId === authRequestId.current) {
        setHasError(true);
        setAuthIsLoading(false);
      }
    }
  };

  const refreshAuth = async () => {
    const requestId = beginAuthRequest();
    try {
      const res = await getRefreshToken();
      if (requestId !== authRequestId.current) return;
      if (res.status !== 200) {
        throw new Error('Unable to refresh the authentication session.');
      }
      const authData = await getAuthData();
      await processAuthData(authData, requestId);
    } catch (error) {
      console.warn(error);
      if (requestId === authRequestId.current) setHasError(true);
    } finally {
      if (requestId === authRequestId.current) {
        setAuthIsLoading(false);
      }
    }
  };

  /**
   * @returns: {Authorization} tokenData
   * @description: Fetches auth data using the httpOnly cookie set by the auth endpoint and initializes UI auth state.
   */
  const handleStoredLogin = useCallback(
    async (force = false) => {
      if (!force && (authRef.current || storedLoginRequested.current)) {
        return Boolean(authRef.current);
      }

      const logoutFlag = sessionStorage.getItem(isLoggingOut);
      if (logoutFlag) {
        if (!force) {
          storedLoginRequested.current = true;
          clearAuthState();
          setAuthIsLoading(false);
          return false;
        }

        sessionStorage.removeItem(isLoggingOut);
      }

      storedLoginRequested.current = true;
      const requestId = ++authRequestId.current;
      setAuthIsLoading(true);
      try {
        const authData = await getAuthData();
        if (requestId !== authRequestId.current) return false;
        const isAuthenticated = authData?.data?.validatedResult?.status === true && Boolean(authData.data.token);
        if (isAuthenticated) {
          if (!await processAuthData(authData, requestId) || requestId !== authRequestId.current) return false;
          if (force) {
            broadcastChannelReload(getAuthChannel());
          }
          return true;
        } else {
          clearAuthState();
          setAuthIsLoading(false);
          return false;
        }
      } catch (error) {
        console.warn(error);
        if (requestId === authRequestId.current) {
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            clearAuthState();
          } else {
            setHasError(true);
          }
          setAuthIsLoading(false);
        }
        return false;
      }
    },
    [processAuthData, getAuthChannel, clearAuthState],
  );


  const handleESNLogin = useCallback((token) => {
    const storedToken = token;

    // User is not logged in but does not have a cookie stored
    // This will allow the login screen to be displayed
    if (!auth && !storedToken) {
      setAuthIsLoading(false);
    }

    if (storedToken && !auth) {
      setAuthIsLoading(true);
      const tokenData = getTokenData(token);
      processAuthData({ data: tokenData }, ++authRequestId.current);
      // processToken(storedToken);
      // auth && setAuthIsLoading(false);
      setCookie(memberCookieName, memberId, 1);
    }
  }, [auth, memberId,processAuthData]);

  /**
   * @description: Logs out user and resets auth context
   */


  const handleLogout = useCallback((shouldBroadcast = true) => {
    if (unauthorizedRedirect.current !== undefined) {
      clearTimeout(unauthorizedRedirect.current);
      unauthorizedRedirect.current = undefined;
    }
    ++authRequestId.current;
    storedLoginRequested.current = true;
    clearAuthState();
    setHasError(false);
    setloginError('');
    setAuthIsLoading(false);
    clearTokenCookie();
    sessionStorage.setItem(isLoggingOut, '2');
    deleteCookie(tokenCookieName);
    deleteCookie(memberCookieName);
    deleteAspCookies();
    localStorage.removeItem(appstatetokenName);
    localStorage.removeItem(storedCommoditiestokenName);
    localStorage.removeItem(themetokenName);
    sessionStorage.removeItem(loginPreviousUrlName);
    sessionStorage.removeItem(relogintokenName);
    if (shouldBroadcast) {
      broadcastChannelLogout(getAuthChannel());
    }
  }, [getAuthChannel, clearAuthState]);

  useEffect(() => {
    if (auth) {
      setAuthenticated(true);
      setAuthIsLoading(false);
      return;
    }
    handleStoredLogin();
  }, [handleStoredLogin, setAuthenticated, auth]);

  /* Value returned to components using context */
  const value = {
    auth,
    authIsLoading,
    hasError,
    loginError,
    isAuthenticated,
    isTokenExpired,
    setHasError,
    token,
    handleLogin,
    handleLimitedPageLogin,
    handleStoredLogin,
    handleESNLogin,
    handleLogout,
    resetAuth,
    refreshAuth,
    getRefreshToken,
  };

  useEffect(() => {
    const onUnauthorized = () => {
      if (unauthorizedRedirect.current !== undefined) return;
      unauthorizedRedirect.current = setTimeout(() => {
        unauthorizedRedirect.current = undefined;
        ++authRequestId.current;
        storedLoginRequested.current = true;
        clearAuthState();
        setAuthIsLoading(false);
        history.push('/login');
      }, 5000);
    };
    window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
    return () => {
      window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
      clearTimeout(unauthorizedRedirect.current);
      unauthorizedRedirect.current = undefined;
    };
  }, [clearAuthState]);

  useEffect(() => {
    const bcAuth = getAuthChannel();
    const onMessage = (event: MessageEvent) => {
      console.log('AuthProvider.EventListener("message") - event', event);

      // flag login error based upon event data
      if (event?.data === BROADCAST_CHANNEL_CODE.loginError) {
        setHasError(true);
        setAuthIsLoading(false);

        return;
      }
      if (event?.data === BROADCAST_CHANNEL_CODE.reload && !window.location.pathname.includes('registration')) {
          ++authRequestId.current;
          sessionStorage.removeItem(isLoggingOut);
          window.location.reload();
        return;
      }
      if (event?.data === BROADCAST_CHANNEL_CODE.logout) {
        handleLogout(false);
        window.location.href = Configurations.REACT_APP_REDIRECT_HOME_URL;
        return;
      }

    };
    bcAuth.addEventListener('message', onMessage);
    return () => {
      bcAuth.removeEventListener('message', onMessage);
      bcAuth.close();
      authChannel.current = undefined;
    };
  }, [getAuthChannel, handleLogout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* istanbul ignore next */
export const MockAuthProvider = ({ auth, children }: any) => (
  <AuthContext.Provider
    value={
      {
        auth: {
          accountId: 1234567890,
          accountLocked: false,
          accountLockedMinutes: 0,
          accountPermissions: '',
          bidVisitExceededUrl: 'bidVisitExceededUrl',
          email: 'hzdkv@example.com',
          firstName: 'Test',
          fullName: 'Test Test',
          hasError: false,
          isBidVisitExceeded: false,
          lastName: 'Test',
          mainContact: true,
          memberBlackListed: false,
          memberCreatedDate: '2019-01-01T00:00:00',
          memberId: 0,
          membershipLevels: '',
          memberStatus: 'Active',
          memberType: MemberType.AgencyBuyer,
          migrated: false,
          opsId: 0,
          primaryMemberId: 1,
          themetokenName: '',
          token: '',
          userName: '',
          userType: AuthUserType.DSUser,
          validatedResult: { status: true, firstName: '', lastName: '' },
          ...auth,
        },
      } as any
    }
  >
    {children}
  </AuthContext.Provider>
);

export default AuthProvider;
